import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  Bold,
  ClassicEditor,
  Essentials,
  Italic,
  Link,
  Paragraph,
  Undo,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import MarkdownIt from 'markdown-it';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';

interface IProps {
  control: Control<any>;
  field: string;
  value: string;
  constantValue: string;
  setValue: UseFormSetValue<any>;
  disableResize?: boolean;
}

// Markdown parser used to migrate legacy Markdown block content to HTML.
// Uses breaks: true to correctly convert hard line breaks (Turndown-style
// "  \n") that may exist in old Markdown content stored in the database.
const legacyMarkdownParser = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: false,
});
legacyMarkdownParser.renderer.rules.strong_open = () => '<strong>';
legacyMarkdownParser.renderer.rules.strong_close = () => '</strong>';
legacyMarkdownParser.renderer.rules.em_open = () => '<em>';
legacyMarkdownParser.renderer.rules.em_close = () => '</em>';

// Detect whether a string already contains HTML markup.
// Content from the new HTML-storage format starts with a block-level tag;
// legacy Markdown content starts with plain text or Markdown syntax.
const isHtmlContent = (text: string): boolean => {
  return text.trimStart().startsWith('<');
};

// Ensure the content is in HTML format, converting from legacy Markdown when
// necessary. HTML content is returned unchanged; Markdown is rendered to HTML
// so the editor can load it through the HTML data processor without losing
// paragraph structure (e.g. empty paragraphs used for blank-line spacing).
const ensureHtmlContent = (text: string): string => {
  if (!text || isHtmlContent(text)) {
    return text;
  }

  // Remove trailing newlines that markdown-it appends after rendering.
  return legacyMarkdownParser.render(text).replace(/\n+$/, '');
};

export const RichTextField: React.FC<IProps> = ({
  field,
  control,
  value,
  constantValue,
  setValue,
  disableResize = false,
}) => {
  // Always store content as HTML so that empty paragraphs (blank-line spacing)
  // survive the round-trip through the editor and the Core formatter.
  const [editorText, setEditorText] = useState(() => ensureHtmlContent(value));
  const [constantField, setConstantField] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: change would break functionality
  useEffect(() => {
    control.register(field);

    if (constantField !== constantValue) {
      setConstantField(constantValue);
      // * Convert legacy Markdown to HTML before passing to the editor — old
      // * content stored in the database may still be in Markdown format.
      setEditorText(ensureHtmlContent(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className={disableResize ? 'no-resize' : ''}>
      <CKEditor
        editor={ClassicEditor}
        data={editorText}
        config={{
          plugins: [Bold, Essentials, Italic, Link, Paragraph, Undo],
          // * CKEditor v42+ requires a licenseKey. 'GPL' enables all features we
          // * use under the open-source license. A commercial key is needed only
          // * for premium features (Track Changes, Collaboration, etc.).
          // * TODO: confirm whether a commercial CKEditor license is required.
          licenseKey: 'GPL',
          toolbar: ['bold', 'italic', 'link', '|', 'undo', 'redo'],
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
          },
        }}
        onBlur={(_event, editor) => {
          // getData() now uses the HTML processor and returns HTML.
          const htmlText = editor.getData();

          setValue(field, htmlText);
          setEditorText(htmlText);
        }}
      />
    </div>
  );
};
